"use client";

import * as React from "react";
import { type Lang, COPY, renderEm } from "../copy.js";
import { GOOGLE_CALENDAR_URL, downloadIcs } from "../lib/calendar.js";
import { SectionEyebrow } from "./SectionEyebrow.js";

const VALID_CODE = "RYE50";

type Attending = "yes" | "no";

function CodeGate({ lang, onUnlock }: { lang: Lang; onUnlock: (code: string) => void }) {
  const t = COPY[lang];
  const [vals, setVals] = React.useState<string[]>(["", "", "", "", ""]);
  const [err, setErr] = React.useState(false);
  const refs = React.useRef<(HTMLInputElement | null)[]>([]);

  const handleChange = (i: number, v: string) => {
    const clean = v
      .replace(/[^a-zA-Z0-9]/g, "")
      .toUpperCase()
      .slice(0, 1);
    const next = [...vals];
    next[i] = clean;
    setVals(next);
    setErr(false);
    if (clean && i < 4) refs.current[i + 1]?.focus();
    if (next.every((x) => x.length === 1)) {
      const code = next.join("");
      if (code === VALID_CODE) {
        setTimeout(() => onUnlock(code), 180);
      } else {
        setErr(true);
      }
    }
  };

  const handleKey = (i: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Backspace" && !vals[i] && i > 0) {
      refs.current[i - 1]?.focus();
    }
    if (e.key === "Enter" && vals.every((x) => x.length === 1)) {
      const code = vals.join("");
      if (code === VALID_CODE) onUnlock(code);
      else setErr(true);
    }
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLDivElement>) => {
    const data = (e.clipboardData.getData("text") || "")
      .replace(/[^a-zA-Z0-9]/g, "")
      .toUpperCase()
      .slice(0, 5);
    if (!data.length) return;
    e.preventDefault();
    const next = ["", "", "", "", ""];
    for (let k = 0; k < data.length; k++) next[k] = data[k] ?? "";
    setVals(next);
    setErr(false);
    if (data === VALID_CODE) setTimeout(() => onUnlock(data), 180);
    else if (data.length === 5) setErr(true);
    refs.current[Math.min(data.length, 4)]?.focus();
  };

  return (
    <div className="lock">
      <div className="lock-spark">✦</div>
      <div className="lock-title">{t.rsvp.lockTitle}</div>
      <p className="lock-sub">{t.rsvp.lockSub}</p>
      <div className="code-input" onPaste={handlePaste}>
        {vals.map((v, i) => (
          <input
            key={i}
            ref={(el) => {
              refs.current[i] = el;
            }}
            value={v}
            onChange={(e) => handleChange(i, e.target.value)}
            onKeyDown={(e) => handleKey(i, e)}
            inputMode="text"
            aria-label={`Code character ${i + 1}`}
            maxLength={1}
            autoComplete="off"
            spellCheck={false}
          />
        ))}
      </div>
      <div style={{ minHeight: 22 }}>{err && <div className="code-err">{t.rsvp.codeErr}</div>}</div>
      <div className="lock-hint">{t.rsvp.lockHint}</div>
    </div>
  );
}

function CheersSVG() {
  return (
    <svg className="cheers-svg" viewBox="0 0 220 220" aria-hidden="true">
      <defs>
        <linearGradient id="whisky" x1="0" x2="0" y1="0" y2="1">
          <stop offset="0%" stopColor="#e6b061" />
          <stop offset="100%" stopColor="#8c5a1e" />
        </linearGradient>
      </defs>
      <g className="glass-l">
        <g transform="translate(48 110)">
          <path
            d="M-30 -40 L30 -40 L26 30 Q 0 36 -26 30 Z"
            fill="rgba(255,255,255,0.2)"
            stroke="var(--ink)"
            strokeWidth={2}
            strokeLinejoin="round"
          />
          <path d="M-27 -6 L27 -6 L24 28 Q 0 34 -24 28 Z" fill="url(#whisky)" />
          <rect
            x={-14}
            y={-2}
            width={12}
            height={10}
            fill="rgba(255,255,255,0.65)"
            stroke="var(--ink)"
            strokeWidth={1.2}
            transform="rotate(-8 -8 3)"
          />
          <rect
            x={2}
            y={2}
            width={10}
            height={10}
            fill="rgba(255,255,255,0.55)"
            stroke="var(--ink)"
            strokeWidth={1.2}
            transform="rotate(10 7 7)"
          />
          <line
            x1={-22}
            y1={-34}
            x2={-20}
            y2={20}
            stroke="rgba(255,255,255,0.75)"
            strokeWidth={2}
            strokeLinecap="round"
          />
        </g>
      </g>
      <g className="glass-r">
        <g transform="translate(172 110)">
          <path
            d="M-30 -40 L30 -40 L26 30 Q 0 36 -26 30 Z"
            fill="rgba(255,255,255,0.2)"
            stroke="var(--ink)"
            strokeWidth={2}
            strokeLinejoin="round"
          />
          <path d="M-27 -6 L27 -6 L24 28 Q 0 34 -24 28 Z" fill="url(#whisky)" />
          <rect
            x={-14}
            y={-2}
            width={12}
            height={10}
            fill="rgba(255,255,255,0.65)"
            stroke="var(--ink)"
            strokeWidth={1.2}
            transform="rotate(-8 -8 3)"
          />
          <rect
            x={2}
            y={2}
            width={10}
            height={10}
            fill="rgba(255,255,255,0.55)"
            stroke="var(--ink)"
            strokeWidth={1.2}
            transform="rotate(10 7 7)"
          />
          <line
            x1={-22}
            y1={-34}
            x2={-20}
            y2={20}
            stroke="rgba(255,255,255,0.75)"
            strokeWidth={2}
            strokeLinecap="round"
          />
        </g>
      </g>
      <g className="clink" transform="translate(110 78)">
        <g fill="var(--accent)">
          <circle cx={0} cy={0} r={3} />
          <path d="M0 -18 L2 -4 L0 0 L-2 -4 Z" />
          <path d="M0 18 L2 4 L0 0 L-2 4 Z" />
          <path d="M-18 0 L-4 2 L0 0 L-4 -2 Z" />
          <path d="M18 0 L4 2 L0 0 L4 -2 Z" />
          <path d="M-12 -12 L-3 -2 L0 0 L-2 -3 Z" />
          <path d="M12 12 L3 2 L0 0 L2 3 Z" />
          <path d="M12 -12 L3 -2 L0 0 L2 -3 Z" />
          <path d="M-12 12 L-3 2 L0 0 L-2 3 Z" />
        </g>
      </g>
    </svg>
  );
}

function Confirmation({
  lang,
  attending,
  onEdit,
}: {
  lang: Lang;
  attending: Attending;
  onEdit: () => void;
}) {
  const t = COPY[lang];
  const block = attending === "yes" ? t.rsvp.confirmYes : t.rsvp.confirmNo;
  return (
    <div className="rsvp">
      <div className="confirm">
        <CheersSVG />
        <h3>{renderEm(block.h)}</h3>
        <p>{block.p}</p>
        <div
          style={{
            marginTop: 24,
            display: "flex",
            gap: 10,
            justifyContent: "center",
            flexWrap: "wrap",
          }}
        >
          {attending === "yes" && (
            <>
              <a className="btn gold" href={GOOGLE_CALENDAR_URL} target="_blank" rel="noreferrer">
                {t.countdown.addGoogle} <span className="arrow">→</span>
              </a>
              <button type="button" className="btn" onClick={downloadIcs}>
                {t.countdown.addApple} <span className="arrow">↓</span>
              </button>
            </>
          )}
          <button type="button" className="btn" onClick={onEdit}>
            {t.rsvp.editRsvp}
          </button>
        </div>
      </div>
    </div>
  );
}

function Form({
  lang,
  code,
  onDone,
}: {
  lang: Lang;
  code: string;
  onDone: (a: Attending) => void;
}) {
  const t = COPY[lang];
  const [name, setName] = React.useState("");
  const [contact, setContact] = React.useState("");
  const [attending, setAttending] = React.useState<Attending>("yes");
  const [plus, setPlus] = React.useState<"0" | "1">("0");
  const [notes, setNotes] = React.useState("");
  const [submitErr, setSubmitErr] = React.useState(false);
  const [sending, setSending] = React.useState(false);

  const valid = name.trim().length >= 2;

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!valid || sending) return;
    setSubmitErr(false);
    setSending(true);
    try {
      const res = await fetch("/api/rsvp", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          code,
          name: name.trim(),
          contact: contact.trim(),
          attending: attending === "yes",
          plusOne: plus === "1",
          notes: attending === "yes" ? notes.trim() : "",
          lang,
        }),
      });
      if (!res.ok) throw new Error("submit_failed");
      setTimeout(() => onDone(attending), 250);
    } catch {
      setSubmitErr(true);
      setSending(false);
    }
  };

  return (
    <form onSubmit={submit} noValidate>
      <div className="field">
        <label className="label" htmlFor="rsvp-name">
          {t.rsvp.name}
        </label>
        <input
          id="rsvp-name"
          type="text"
          required
          placeholder={t.rsvp.namePh}
          value={name}
          onChange={(e) => setName(e.target.value)}
        />
      </div>

      <div className="field">
        <label className="label" htmlFor="rsvp-contact">
          {t.rsvp.contact}
        </label>
        <input
          id="rsvp-contact"
          type="text"
          placeholder={t.rsvp.contactPh}
          value={contact}
          onChange={(e) => setContact(e.target.value)}
        />
      </div>

      <div className="field">
        <label className="label" id="rsvp-attending-label">
          {t.rsvp.attending}
        </label>
        <div className="attend-toggle" role="group" aria-labelledby="rsvp-attending-label">
          <button
            type="button"
            data-val="yes"
            aria-pressed={attending === "yes"}
            onClick={() => setAttending("yes")}
          >
            {t.rsvp.yes}
          </button>
          <button
            type="button"
            data-val="no"
            aria-pressed={attending === "no"}
            onClick={() => setAttending("no")}
          >
            {t.rsvp.no}
          </button>
        </div>
      </div>

      {attending === "yes" && (
        <>
          <div className="field">
            <label className="label">{t.rsvp.plusone}</label>
            <div className="plusone">
              {t.rsvp.plusopts.map((opt) => (
                <button
                  key={opt.v}
                  type="button"
                  aria-pressed={plus === opt.v}
                  onClick={() => setPlus(opt.v)}
                >
                  {opt.label}
                  <span className="sub">{opt.sub}</span>
                </button>
              ))}
            </div>
          </div>

          <div className="field">
            <label className="label" htmlFor="rsvp-notes">
              {t.rsvp.notes}
            </label>
            <textarea
              id="rsvp-notes"
              placeholder={t.rsvp.notesPh}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
            />
          </div>
        </>
      )}

      {submitErr && <div className="field-error">{t.rsvp.submitErr}</div>}

      <div className="submit-row">
        <span className="hint">{t.rsvp.privacy}</span>
        <button type="submit" className="btn primary" disabled={!valid || sending}>
          {sending ? t.rsvp.submitSending : t.rsvp.submit} <span className="arrow">→</span>
        </button>
      </div>
    </form>
  );
}

export function RsvpBlock({ lang }: { lang: Lang }) {
  const t = COPY[lang];
  const [code, setCode] = React.useState<string | null>(null);
  const [confirmed, setConfirmed] = React.useState<Attending | null>(null);

  return (
    <section className="block">
      <div className="wrap">
        <SectionEyebrow num={t.rsvp.eyebrow.num} name={t.rsvp.eyebrow.name} />
        <h2 className="hd">{renderEm(t.rsvp.h)}</h2>
        <p className="lead">{t.rsvp.lead}</p>
        {confirmed ? (
          <Confirmation lang={lang} attending={confirmed} onEdit={() => setConfirmed(null)} />
        ) : (
          <div className="rsvp">
            {code ? (
              <Form lang={lang} code={code} onDone={setConfirmed} />
            ) : (
              <CodeGate lang={lang} onUnlock={setCode} />
            )}
          </div>
        )}
      </div>
    </section>
  );
}
